import React, {useEffect, useRef, useState} from "react";
import SVG from "react-inlinesvg";
import {Form, Modal, OverlayTrigger, Tooltip} from "react-bootstrap";
import Select from 'react-select';
import cogoToast from 'cogo-toast';
import {shallowEqual, useDispatch, useSelector} from "react-redux";
import {convertToNumber, formatAmount} from "../../../../util/Helpers";
import {toAbsoluteUrl} from "../../../../../_metronic/_helpers";
import * as handlers from "../../../UserManagement/_redux/selected-user/selectedUserHandlers"
import {useCreateOrderMutation} from "../../../../../redux/apiSlice";

const NewOrderModal = ({showNewOrderModal, onHide, provider, match, refreshOrders}) => {

    const dispatch = useDispatch();
    /* Get Loading status, selected instrument, search instrument result from redux */
    const {isLoading, searchedInstrument, selectedInstrument, data: {profile}} = useSelector(({selectedUser}) => selectedUser, shallowEqual);

    const [selectedOption, setSelectedOption] = useState(null);
    const [order, setOrder] = useState({side: "BUY", type: "MARKET", price: 0, amount: 0, quantity: 0, priority: null});


    // Instrument Search
    const timeout = useRef();
    const searchInput = input => {
        clearTimeout(timeout.current);
        if (input.trim() !== "") {
            timeout.current = setTimeout(_ => dispatchAction(input.trim()), 1000)
        }
    }

    // Fire Action after Delay
    const dispatchAction = (input) => {
        dispatch(handlers.getSearchInstruments(input, provider));
    }

    // Instrument Selection
    const instrumentSelected = val => {
        setSelectedOption(val);
        if (val) {
            dispatch(handlers.getInstrumentDetails(val?.value));
        }
    }

    // Order Side Switch
    const orderSideChange = e => {
        setOrder({side: e.currentTarget.value, type: "MARKET", price: 0, amount: 0, quantity: 0, priority: null});
    }

    // Order Type Switch
    const orderTypeChange = e => {
        setOrder({...order, type: e.currentTarget.value, price: 0, amount: 0, quantity: 0, priority: null});
    }

    // Amount Input Change
    const amountChange = e => {
        const priority = order.type === "MARKET" ? "amount" : null;
        const amount = e.currentTarget.value;
        const divisor = order.side === "BUY" ? selectedInstrument?.quote?.ask : selectedInstrument?.quote?.bid
        const quantity = (convertToNumber(amount) / divisor).toLocaleString(undefined, {maximumFractionDigits: 10});
        setOrder({...order, amount, quantity, priority});
    }

    // Target price input change
    const priceChange = e => {
        const price = e.currentTarget.value;
        setOrder({...order, price});
    }

    // Quantity price input change
    const quantityChange = e => {
        const priority = order.type === "MARKET" ? "quantity" : null;
        const quantity = e.currentTarget.value;
        const multiplier = order.side === "BUY" ? selectedInstrument?.quote?.ask : selectedInstrument?.quote?.bid
        if (order.type === "MARKET") {
            const amount = (convertToNumber(quantity) * multiplier)?.toLocaleString(undefined, {maximumFractionDigits: 2});
            setOrder({...order, amount, quantity, priority});
        } else {
            setOrder({...order, quantity, priority});
        }
    }

    // Input Reformatting
    const reformatIput = field => e => {
        setOrder({
            ...order,
            [field]: convertToNumber(order[field]).toLocaleString(undefined, {maximumFractionDigits: 10})
        });
    }

    const [
        createOrder,
        {
            isLoading: isCreating,
            isSuccess: creationSuccessful,
        }
    ] = useCreateOrderMutation()

    /* Close Modal on successful update */
    useEffect(() => {
        if (creationSuccessful) {
            cogoToast.success("Created 'Order Creation' Action Successfully")
            onHide();
            refreshOrders();
        }
    }, [creationSuccessful, onHide, refreshOrders]);

    // Create Order for User
    const create = _ => {

        const type = order.type;
        const side = order.side;
        const priority = order.priority;
        const amount = convertToNumber(order.amount);
        const price = convertToNumber(order.price);
        const quantity = convertToNumber(order.quantity);

        if (type === "MARKET" && amount <= 0) {
            cogoToast.error(`Valid Amount required for ${type} ${side}`);
        } else if ((type === "LIMIT" || type === "STOP") && (price <= 0 || quantity <= 0)) {
            cogoToast.error(`Valid Target Price and Quantity required for ${type} ${side}`);
        } else {

            let data = {
                meta: {...selectedInstrument, ...profile.basic, provider},
                instrumentId: selectedInstrument.id,
                side,
                type,
                priority,
                userId: match.params.userId,
                accountId: match.params.accountId
            };
            if (type === "LIMIT" || type === "STOP") {
                data = {...data, price, quantity};
            } else {
                data = {...data, amount, quantity};
            }

            createOrder(data);
        }

    }

    // Reset order state
    useEffect(() => {
        setOrder({side: "BUY", type: "MARKET", price: 0, amount: 0, quantity: 0})
    }, [selectedOption]);


    return (
        <Modal
            show={showNewOrderModal}
            onHide={onHide}
            aria-labelledby="contained-modal-title-vcenter"
            backdrop={isCreating ? "static" : true}
            keyboard={!isCreating}
            onExited={_ => setSelectedOption(null)}
        >

            <Modal.Header>
                <Modal.Title id="contained-modal-title-vcenter">
                    <span className={`svg-icon svg-icon-lg svg-icon-primary mr-3`}>
                        <SVG
                            title=""
                            src={toAbsoluteUrl("/media/svg/icons/Shopping/Cart3.svg")}
                        />
                    </span>
                    Create New Order
                </Modal.Title>
                <button type="button" className="close" onClick={onHide}>
                    <span aria-hidden="true" className="d-block">×</span>
                    <span className="sr-only">Close</span>
                </button>
            </Modal.Header>
            <Modal.Body
                className="px-10 py-2"
            >
                <div className="row">
                    <div className="col-lg-12 py-4">
                        <Select
                            value={selectedOption}
                            onChange={instrumentSelected}
                            options={searchedInstrument?.map(opt => ({
                                value: opt.instrumentId,
                                label: `(${opt.symbol}) ${opt.name}`
                            })) ?? []}
                            onInputChange={searchInput}
                            isLoading={isLoading}
                            isClearable={true}
                            isSearchable={true}
                            placeholder="Search Instrument..."
                            styles={{
                                control: (base) => ({
                                    ...base,
                                    borderColor: "hsl(0, 0%, 80%)",
                                    boxShadow: 0,
                                    '&:hover': {
                                        borderColor: "hsl(0, 0%, 80%)",
                                    }
                                })
                            }}
                        />
                    </div>
                </div>
                <div
                    className={`px-3 ${selectedInstrument && searchedInstrument && selectedOption && !isLoading ? '' : 'd-none'}`}>
                    <div className="row mt-4">
                        <div className="col-lg py-4">
                            <h6 className="text-primary">Ask</h6>
                            <span
                                className="font-weight-bold text-muted">{formatAmount(selectedInstrument?.currency, selectedInstrument?.quote?.ask)}</span>
                        </div>
                        <div className="col-lg py-4">
                            <h6 className="text-primary">Bid</h6>
                            <span
                                className="font-weight-bold text-muted">{formatAmount(selectedInstrument?.currency, selectedInstrument?.quote?.bid)}</span>
                        </div>
                        <div className="col-lg py-4">
                            <h6 className="text-primary">Last</h6>
                            <span
                                className="font-weight-bold text-muted">{formatAmount(selectedInstrument?.currency, selectedInstrument?.quote?.last)}</span>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-lg py-4">
                            <h6 className="text-primary">Open</h6>
                            <span
                                className="font-weight-bold text-muted">{formatAmount(selectedInstrument?.currency, selectedInstrument?.quote?.open)}</span>
                        </div>
                        <div className="col-lg py-4">
                            <h6 className="text-primary">High</h6>
                            <span
                                className="font-weight-bold text-muted">{formatAmount(selectedInstrument?.currency, selectedInstrument?.quote?.high)}</span>
                        </div>
                        <div className="col-lg py-4">
                            <h6 className="text-primary">Low</h6>
                            <span
                                className="font-weight-bold text-muted">{formatAmount(selectedInstrument?.currency, selectedInstrument?.quote?.low)}</span>
                        </div>
                    </div>

                    <div className="row mt-5">
                        <div className="col-lg-5 py-4">
                            <h6 className="mb-0 pt-3">Order Side</h6>
                        </div>
                        <div className="col-lg py-4">
                            <Form.Control
                                required
                                as="select"
                                className="mr-sm-2"
                                custom
                                value={order.side}
                                onChange={orderSideChange}
                            >
                                <option value="BUY">Buy</option>
                                <option value="SELL">Sell</option>
                            </Form.Control>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-lg-5 py-4">
                            <h6 className="mb-0 pt-3">Order Type</h6>
                        </div>
                        <div className="col-lg py-4">
                            <Form.Control
                                required
                                as="select"
                                className="mr-sm-2"
                                custom
                                value={order.type}
                                onChange={orderTypeChange}
                            >
                                <option value="MARKET">Market</option>
                                <option value="LIMIT">Limit</option>
                                {order.side === "SELL" ? <option value="STOP">Stop Loss</option> : ""}
                            </Form.Control>
                        </div>
                    </div>

                    <div className={`row ${order.type === "LIMIT" || order.type === "STOP" ? "" : "d-none"}`}>
                        <div className="col-lg-5 py-4">
                            <h6 className="mb-0 pt-3">Target Price in {selectedInstrument?.currency}</h6>
                        </div>
                        <div className="col-lg py-4">
                            <Form.Control
                                type="text"
                                onBlur={reformatIput("price")}
                                onChange={priceChange}
                                value={order.price}
                                autoComplete="off"
                            />
                        </div>
                    </div>

                    <div className={`row ${order.type === "MARKET" ? "" : "d-none"}`}>
                        <div className="col-lg-5 py-4">
                            <h6 className="mb-0 pt-3 d-inline-block">Amount in {selectedInstrument?.currency}</h6>
                            <OverlayTrigger
                                overlay={
                                    <Tooltip>Estimated Amount</Tooltip>
                                }
                            >
                                <button
                                    className={`btn svg-icon svg-icon-sm svg-icon-info ml-3 p-0 pb-3 ${!order.priority || (order.type === "MARKET" && order.priority === "amount") ? "d-none" : ""}`}>
                                    <SVG
                                        title=" "
                                        src={toAbsoluteUrl("/media/svg/icons/Code/Question-circle.svg")}
                                    />
                                </button>
                            </OverlayTrigger>
                        </div>
                        <div className="col-lg py-4">
                            <Form.Control
                                type="text"
                                onBlur={reformatIput("amount")}
                                onChange={amountChange}
                                value={order.amount}
                                autoComplete="off"
                            />
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-lg-5 py-4">
                            <h6 className="mb-0 pt-3 d-inline-block">Quantity of {selectedInstrument?.symbol} </h6>
                            <OverlayTrigger
                                overlay={
                                    <Tooltip>Estimated Quantity</Tooltip>
                                }
                            >
                                <button
                                    className={`btn svg-icon svg-icon-sm svg-icon-info ml-3 p-0 pb-3 ${!order.priority || (order.type === "MARKET" && order.priority === "quantity") ? "d-none" : ""}`}>
                                    <SVG
                                        title=" "
                                        src={toAbsoluteUrl("/media/svg/icons/Code/Question-circle.svg")}
                                    />
                                </button>
                            </OverlayTrigger>
                        </div>
                        <div className="col-lg py-4">
                            <Form.Control
                                type="text"
                                onBlur={reformatIput("quantity")}
                                onChange={quantityChange}
                                value={order.quantity}
                                autoComplete="off"
                            />
                        </div>
                    </div>

                </div>
            </Modal.Body>
            <Modal.Footer className="justify-content-centerx">
                <button
                    type="button"
                    onClick={onHide}
                    className="btn btn-light btn-elevate px-4"
                >
                    Cancel
                </button>
                <> </>
                <button
                    type="button"
                    disabled={!(selectedInstrument && searchedInstrument && selectedOption && !isCreating)}
                    className="btn btn-primary btn-elevate px-4 ml-4"
                    onClick={create}
                >
                    Create Order
                </button>
            </Modal.Footer>
        </Modal>

    );
}

export default NewOrderModal;